/* Meal Log Card Component */
import React, { useEffect, useMemo, useState } from "react";
import { useLang } from "../contexts/LangContext";
import { FishConsumptionUnit, MealRecord } from "../types/data";
import { convertInCurrentLang, convertNumberInCurrentLang } from "../utils/misc";
import FishLog from "./FishLog";
import NutrientMeter from "./NutrientMeter";
import { fishDatabase } from "../data/fish_db";
import { storageService } from "../services/storage.service";

// Group fish items by species only
interface GroupedFishItem {
  fish_id: string;
  fish_name: string;
  groupKey: string;
  items: FishConsumptionUnit[];
  total_weight: number;
  summary: string; // e.g., "1 whole (Fried), 1 head piece (curry), 2 middle pieces (curry)"
}

const MealLog = ({
  meal,
  allowEdit = false,
  allowDelete = false,
  onEdit = () => {},
  onDelete = () => {},
}: {
  meal: MealRecord;
  allowEdit?: boolean;
  allowDelete?: boolean;
  onEdit?: (item: FishConsumptionUnit) => void;
  onDelete?: (item: FishConsumptionUnit) => void;
}) => {
  const { t, lang } = useLang();
  const [sessionInfoExpanded, setSessionInfoExpanded] = useState(false);
  const [showNutrientOutcome, setShowNutrientOutcome] = useState(true);
  const [showNutrientDRI, setShowNutrientDRI] = useState(false);

  // Check if nutrients should be shown based on settings
  useEffect(() => {
    storageService.getSettings().then(settings => {
      setShowNutrientOutcome(settings.showNutrientOutcome ?? true);
      setShowNutrientDRI(settings.showNutrientDRI ?? false);
    }).catch(() => {});
  }, []);

  // Calculate total session duration
  const sessionSummary = useMemo(() => {
    if (!meal.log_sessions || meal.log_sessions.length === 0) {
      return null;
    }

    let totalDurationMs = 0;
    meal.log_sessions.forEach(session => {
      if (session.started_at && session.completed_at) {
        const duration = new Date(session.completed_at).getTime() - new Date(session.started_at).getTime();
        totalDurationMs += duration;
      }
    });

    const totalSeconds = Math.floor(totalDurationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return {
      sessionCount: meal.log_sessions.length,
      minutes,
      seconds,
      totalSeconds,
    };
  }, [meal.log_sessions]);

  // Group fish items by species only
  const groupedItems = useMemo(() => {
    const groups = new Map<string, GroupedFishItem>();
    
    meal.fish_items.forEach((item) => {
      // Known fish group by species id. "Other" fish all share fish_id
      // "other", so split them by their distinct fish_name to keep entries
      // separate (e.g., two differently named "Other" fish in one meal).
      const key = item.fish_id === "other" ? `other::${item.fish_name}` : item.fish_id;
      const existing = groups.get(key);
      
      if (existing) {
        existing.items.push(item);
        existing.total_weight += item.estimated_weight || 0;
      } else {
        groups.set(key, {
          fish_id: item.fish_id,
          fish_name: item.fish_name,
          groupKey: key,
          items: [item],
          total_weight: item.estimated_weight || 0,
          summary: ''
        });
      }
    });
    
    // Generate summary for each group
    groups.forEach((group) => {
      // Count by portion_type + piece_type + recipe combination
      const countMap = new Map<string, { count: number; portion_type: string; piece_type?: string; recipe: string }>();
      
      group.items.forEach((item) => {
        const pieceKey = item.piece_type || 'none';
        const key = `${item.portion_type}_${pieceKey}_${item.recipe}`;
        const existing = countMap.get(key);
        
        if (existing) {
          existing.count += item.number_of_portions || 1;
        } else {
          countMap.set(key, {
            count: item.number_of_portions || 1,
            portion_type: item.portion_type || 'unknown',
            piece_type: item.piece_type,
            recipe: item.recipe
          });
        }
      });
      
      // Build summary string
      const summaryParts: string[] = [];
      countMap.forEach((value) => {
        const count = convertNumberInCurrentLang(lang, value.count);
        let portionText = '';
        
        if (value.portion_type === 'whole') {
          // For whole fish: "1টি সম্পূর্ণ মাছ" or "1 whole"
          if (lang === 'bn') {
            portionText = `${count}টি ${t('portionType_whole')}`;
          } else {
            portionText = `${count} ${t('portionType_whole')}`;
          }
        } else if (value.portion_type === 'piece' && value.piece_type) {
          const pieceTypeText = t(`pieceType_${value.piece_type}` as any);
          // For pieces: "২ টুকরা মাঝের অংশ" or "2 middle pieces"
          if (lang === 'bn') {
            portionText = `${count} ${t('reviewLog_piece')} ${pieceTypeText}`;
          } else {
            portionText = value.count > 1
              ? `${count} ${pieceTypeText} ${t('reviewLog_pieces')}`
              : `${count} ${pieceTypeText} ${t('reviewLog_piece')}`;
          }
        } else if (value.portion_type === 'portion') {
          // For portions: "২টি অংশ" or "2 portions"
          if (lang === 'bn') {
            portionText = `${count}টি ${t('portionType_portion')}`;
          } else {
            portionText = `${count} ${t('portionType_portion')}`;
          }
        } else {
          portionText = `${count} ${t(`portionType_${value.portion_type}` as any)}`;
        }
        
        const recipeText = t(`whichRecipe_${value.recipe}` as any);
        summaryParts.push(`${portionText} (${recipeText})`);
      });
      
      group.summary = summaryParts.join(', ');
    });
    
    return Array.from(groups.values());
  }, [meal.fish_items, lang, t]);

  return (
    <div key={meal.id} className="mt-4 border-t pt-2">
      <div className="flex items-center justify-center rounded-full button-bg-grad-3 text-white text-xl font-bold text-center px-8 py-2 mb-2">
        {t(`mealType_${meal.meal_type}`)}
      </div>
      <p className="text-sky-600">
        {t("reviewLog_TotalItems")}:{" "}
        <strong>
          {convertNumberInCurrentLang(lang, groupedItems.length)}
        </strong>
      </p>

      {groupedItems.map((group) => (
        <div
          key={group.groupKey}
          className="border-t pt-2 mt-2"
        >
          {/* Group header */}
          <div className="bg-blue-100 rounded-lg p-3 mb-2">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800">{group.fish_name}</h3>
                <div className="text-sm text-gray-600 mt-1">
                  {group.summary}
                </div>
                <div className="text-lg font-semibold text-blue-700 mt-1">
                  {t("reviewLog_TotalWeight") || "Total"}: {convertNumberInCurrentLang(lang, Math.round(group.total_weight))} {t("unit_gm")}
                </div>
              </div>
            </div>
          </div>          

          {/* Individual items in group */}
          {group.items.map((item, itemIndex) => (
            <div
              key={item.id}
              className="mb-4"
            >
              <div className="flex flex-col items-start justify-between gap-2">
                <div className="flex items-center justify-between w-full">
                  {group.items.length > 0 && (
                    <span className="text-base font-bold text-gray-500 font-semibold ml-1">
                      {t("reviewLog_FishItem")} #{convertNumberInCurrentLang(lang, itemIndex + 1)}
                    </span>
                  )}      
                  {!allowEdit && allowDelete && onDelete && (
                    <button
                      className="flex items-center gap-1 rounded-full border border-gray-300 text-red-600 hover:text-red-800 px-2 py-1 text-xs"
                      onClick={() => onDelete(item)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="size-3"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                        />
                      </svg>
                      {t("reviewFishItem_DeleteItem")}
                    </button>
                  )}            
                </div> 
                <FishLog item={item} compact={true} showNutrients={false} />               
              </div>

              {/* Edit/Delete buttons for editable mode */}
              {allowEdit && allowDelete && (
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => onEdit(item)}
                    className="button-xs button-bg-grad-2 flex items-center gap-1 !px-3 !text-xs"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="size-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                      />
                    </svg>
                    {t("reviewFishItem_Edit")}
                  </button>
                  <button
                    className="button-xs button-bg-grad-red flex items-center gap-1 !px-3 !text-xs"
                    onClick={() => onDelete(item)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="size-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                      />
                    </svg>
                    {t("reviewFishItem_Delete")}
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Combined Nutrient Chart for the group */}
          {showNutrientOutcome && fishDatabase.some((f) => f.id === group.fish_id) && (
          <div className="bg-gray-100 rounded-lg p-4 pb-3">
            <h4 className="text-base font-semibold text-gray-700 mb-2 uppercase">
              {t("reviewLog_NutritionalValue") || "Nutritional Value"}
            </h4>
            <NutrientMeter
              fishes={group.items.map(item => ({
                fishId: item.fish_id,
                amount_in_gm: item.estimated_weight || 0
              }))}
              showDRI={showNutrientDRI}
            />
          </div>
          )}
        </div>
      ))}
      {/* Updated at */}
      {meal.updated_at && (
        <p className="text-xs text-gray-500 mt-2 text-center">
          {t("reviewLog_UpdatedAt")}:{" "}{convertInCurrentLang(lang, new Date(meal.updated_at).toLocaleString())}
        </p>
      )}  
      
      {/* Session Info */}
      {sessionSummary && sessionSummary.totalSeconds > 0 && (
        <div className="text-center">
          <button
            onClick={() => setSessionInfoExpanded(!sessionInfoExpanded)}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              className={`size-3 transition-transform ${sessionInfoExpanded ? 'rotate-90' : ''}`}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
            <span>
              {t("mealLog_SessionTime")}: {convertNumberInCurrentLang(lang, sessionSummary.minutes)} {t("mealLog_Minutes")} {convertNumberInCurrentLang(lang, sessionSummary.seconds)} {t("mealLog_Seconds")}
              {sessionSummary.sessionCount > 1 && (
                <span className="ml-1">({t("mealLog_TotalIn")} {convertNumberInCurrentLang(lang, sessionSummary.sessionCount)} {t("mealLog_Sessions")})</span>
              )}
            </span>
          </button>
          
          {sessionInfoExpanded && (
            <div className="mt-1 flex flex-col space-y-2 text-xs text-gray-600">
              {meal.log_sessions?.map((session, index) => {
                const durationMs = session.completed_at && session.started_at
                  ? new Date(session.completed_at).getTime() - new Date(session.started_at).getTime()
                  : 0;
                const durationSeconds = Math.floor(durationMs / 1000);
                const mins = Math.floor(durationSeconds / 60);
                const secs = durationSeconds % 60;
                
                // Get fish names for this session
                const sessionFishItems = meal.fish_items.filter(item => 
                  session.fish_item_ids?.includes(item.id)
                );
                const fishNames = sessionFishItems.map(item => {
                  // Use English name for English, local/regular name for Bengali
                  return lang === 'en' ? (item.fish_english_name || item.fish_name) : item.fish_name;
                });
                const uniqueFishNames = Array.from(new Set(fishNames)); // Remove duplicates
                
                return (
                  <div key={index} className="border-l-2 border-blue-300 pl-3 py-1 text-left inline-block">
                    <div className="font-semibold">
                      {t("mealLog_Session")} {convertNumberInCurrentLang(lang, index + 1)}: {convertNumberInCurrentLang(lang, mins)} {t("mealLog_Minutes")} {convertNumberInCurrentLang(lang, secs)} {t("mealLog_Seconds")}
                    </div>
                    <div className="text-gray-500">
                      {uniqueFishNames.length > 0 ? uniqueFishNames.join(', ') : `${t("mealLog_FishItems")}: ${convertNumberInCurrentLang(lang, session.fish_item_ids?.length || 0)}`}
                    </div>
                    {session.started_at && (
                      <div className="text-gray-500">
                        {convertInCurrentLang(lang, new Date(session.started_at).toLocaleString())}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MealLog;
